import { Button } from '@heidi/design-system/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

interface ExpandableTextProps {
  text: string;
  className?: string;
}

const messages = defineMessages({
  collapse: {
    defaultMessage: 'Collapse',
    id: 'N+rapPsYxV',
    description: 'Accessible label and tooltip to collapse truncated text',
  },
  expand: {
    defaultMessage: 'Expand',
    id: 'ZKGQCLwBtm',
    description: 'Accessible label and tooltip to expand truncated text',
  },
});

/**
 * ExpandableText component that uses CSS line-clamp-2 for visual truncation.
 * This component intentionally performs a CSS two-line visual clamp (not character truncation).
 * The expand/collapse button appears only when text overflows beyond two lines.
 */
export const ExpandableText = ({ text, className }: ExpandableTextProps) => {
  const intl = useIntl();
  const textId = useId();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState<boolean | undefined>(undefined);
  const textRef = useRef<HTMLParagraphElement>(null);

  const checkClamping = useCallback(() => {
    const element = textRef.current;
    if (!element) return;

    try {
      // Create a hidden duplicate to measure clamped height
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.position = 'absolute';
      clone.style.visibility = 'hidden';
      clone.style.width = `${element.clientWidth}px`;
      clone.classList.add('line-clamp-2');

      const parent = element.parentElement;
      if (!parent) {
        clone.remove();
        return;
      }

      parent.appendChild(clone);

      const shouldShowButton = clone.scrollHeight > clone.clientHeight;
      clone.remove();

      setIsClamped(shouldShowButton);
    } catch {
      // Silently handle DOM manipulation errors
      // This can happen if the element is unmounted during measurement
      return;
    }
  }, []);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    checkClamping();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(checkClamping);
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    } else {
      window.addEventListener('resize', checkClamping);

      return () => {
        window.removeEventListener('resize', checkClamping);
      };
    }
  }, [text, checkClamping]);

  const toggleMessage = isExpanded ? messages.collapse : messages.expand;

  return (
    <div className="flex items-end gap-1">
      <TypographyP2
        id={textId}
        ref={textRef}
        className={cn(!isExpanded && 'line-clamp-2', className)}
      >
        {text}
      </TypographyP2>
      {isClamped === true && (
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <Button
              type="button"
              disableAutomaticSvgSizing
              size="xs"
              variant="ghost"
              className="h-fit min-w-fit shrink-0 p-0.5 px-1 text-text-secondary"
              aria-expanded={isExpanded}
              aria-controls={textId}
              aria-label={intl.formatMessage(toggleMessage)}
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
            >
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <FormattedMessage {...toggleMessage} />
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
};
